/**
 * Learning paths — the browse surface.
 *
 * Built as Discover's SIBLING, not as a second catalogue with its own look. The prototype's
 * `#screen-programs` reuses `.disc__hero` verbatim from its courses catalogue and then
 * swaps the entity in the grid, and its own page spec says so in the first line: "Sister
 * surface to /discover. Same hero shape, same browse pattern; the entities are multi-course
 * pathways." So this page reuses `.cat` and the catalogue card wholesale and adds only what
 * a path has and a course does not — a step count, and a progress bar once you are in it.
 *
 * WHAT IS NOT HERE, AND WHY. The prototype carries a five-select filter bar (organisation,
 * format, state, language, sort), a stat strip and pagination. None of them are built:
 *
 *   organisation  one academy per hostname here, so every path on the page is already
 *                 this org's — the filter would have one option
 *   language      Open edX has no per-path language, and the prototype's is fixture text
 *   state/sort    meaningful over a catalogue of hundreds; this is a handful of paths,
 *                 and controls that filter nothing teach people the page is broken
 *   pagination    the seam returns the whole list; there is nothing to page
 *
 * They come back when a real backend makes them answerable. Search stays, because it is the
 * one control that still works over a short list.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banner, Button, EmptyState, SearchField, Spinner } from '../../shared/ui';
import { text } from '../../shared/lib/config';
import { loadSession, signIn } from '../../shared/lib/session';
import {
  listLearningPaths,
  PATH_SEAM_KEY,
  PATHS_ARE_FIXTURES,
  PATHS_REQUIRE_SIGN_IN,
  type LearningPathView,
} from '../../shared/lib/paths';
// The card is `PathCard.tsx`'s, not a private copy. This page and that file each held a
// character-identical definition for a day; two copies of a card is how two grids start
// disagreeing about what "in progress" looks like.
import { PathCard } from './PathCard';
import { usePathwayPrices } from '../../shared/lib/usePricing';
import './learner.css';
import './paths.css';

export default function Paths() {
  const [search, setSearch] = useState('');

  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const signedIn = session.data?.authenticated === true;

  // Gated on the session so a path never renders "not enrolled" for a second and then
  // flips — the enrolment state is the whole point of the card, and a card that changes
  // its mind under the reader is worse than one that arrives late.
  const paths = useQuery({
    queryKey: PATH_SEAM_KEY(signedIn),
    queryFn: () => listLearningPaths(signedIn),
    enabled: !session.isPending,
    retry: false,
  });

  const visible = useMemo(() => filter(paths.data ?? [], search), [paths.data, search]);

  /**
   * Prices for the cards that survived the search, not for every path the academy has.
   *
   * Keyed on the VISIBLE list rather than `paths.data`, so typing in the search box narrows
   * the batch instead of holding a request for rows nobody can see. The sorted-joined key
   * means the unfiltered grid's entry stays cached while a search is refined and is reused
   * the moment the box is cleared.
   */
  const prices = usePathwayPrices(useMemo(() => visible.map((view) => view.path.key), [visible]));

  return (
    <main className="cat">
      <header className="cat__band">
        <div className="cat__band-inner">
          <p className="cat__eyebrow">{text('nav_paths', 'Learning Paths')}</p>
          <h1>{text('paths_heading', 'Build mastery, not just modules')}</h1>
          <p className="cat__lede">
            {text(
              'paths_subheading',
              'A learning path bundles related courses into one sequence, with its own rules and its own certificate at the end.',
            )}
          </p>
          <div className="cat__search">
            <SearchField value={search} onChange={setSearch} placeholder="Search learning paths…" />
          </div>
        </div>
      </header>

      <div className="cat__body">
        {PATHS_ARE_FIXTURES && <FixtureNotice />}

        {session.isPending || paths.isPending ? (
          <Spinner label="Loading learning paths…" />
        ) : !signedIn && PATHS_REQUIRE_SIGN_IN ? (
          /* The pathways API answers a signed-out visitor with 401 — there is no anonymous
             pathway catalogue. Saying so is the honest empty page; "this academy has not
             published a learning path" would be a statement we have not checked and, on
             every academy that has one, false. */
          <EmptyState
            title="Sign in to see this academy's learning paths"
            body="Learning paths are shown to people with an account here, because a path tracks where you are in it."
            action={
              <Button variant="primary" onClick={() => signIn()}>
                Sign in
              </Button>
            }
          />
        ) : paths.isError ? (
          <Banner tone="danger">
            <strong>Learning paths could not be loaded.</strong> The academy did not answer.
            This is not something you have done — nothing has changed, so retrying is safe.
            <div className="cat__banner-action">
              <Button variant="secondary" onClick={() => void paths.refetch()}>
                Try again
              </Button>
            </div>
          </Banner>
        ) : visible.length === 0 ? (
          search ? (
            <EmptyState
              title={`Nothing matches “${search}”`}
              body="Try a shorter word, or clear the search to see every path."
              action={
                <Button variant="secondary" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No learning paths yet"
              body="This academy has not published a learning path. They appear here the moment one goes live."
            />
          )
        ) : (
          <>
            <p className="cat__count" role="status">
              {visible.length} {visible.length === 1 ? 'learning path' : 'learning paths'}
            </p>

            <div className="course-grid">
              {visible.map((view) => (
                <PathCard key={view.path.key} view={view} prices={prices} />
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

/**
 * Says out loud that the pathways backend is not installed.
 *
 * It is driven by `PATHS_ARE_FIXTURES` rather than written into the markup so that wiring
 * the real endpoints removes it automatically. A notice someone has to remember to delete
 * is a notice that ships.
 */
function FixtureNotice() {
  return (
    <Banner tone="info">
      <strong>These learning paths are sample data.</strong> The pathways backend is not
      installed on this academy yet, so the paths below are examples built over your real
      courses. Every course they link to is real; the paths themselves, and any progress
      shown against them, are not.
    </Banner>
  );
}

/* -------------------------------------------------------------------- helpers ----- */

/**
 * Client-side search, over title and description.
 *
 * Client-side because the seam returns the whole list — there is no server to ask. When the
 * real endpoint arrives and the list grows past a page, this becomes a query parameter and
 * the debounce Discover already has comes with it.
 */
function filter(paths: LearningPathView[], search: string): LearningPathView[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return paths;

  return paths.filter(
    (view) =>
      view.path.title.toLowerCase().includes(needle) ||
      view.path.description.toLowerCase().includes(needle),
  );
}
